import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  Box,
  Chip,
  Avatar,
  Collapse,
  IconButton,
  Button,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, Save } from '@mui/icons-material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useParams } from 'react-router-dom';
import { useCompetencia } from '../../context/CompetenciaContext';
import { formatMeetEntryMark } from '../../utils/meetUtils';

// Helper to avoid rendering raw objects as React children
const toText = (v, fallback = '—') => {
  if (v === null || v === undefined) return fallback;
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object') return v.nombre ?? v.name ?? v.label ?? fallback;
  return String(v ?? fallback);
};

/* ----------------------------------------------------------------------
   Componente SortableAtleta
---------------------------------------------------------------------- */
const SortableAtleta = ({ atleta, containerId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: atleta.id,
    data: { sortable: { containerId } },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: atleta.isPlaceholder ? 'default' : 'grab',
    border: atleta.isPlaceholder ? '1px dashed #ccc' : 'none',
    backgroundColor: atleta.isPlaceholder ? '#f9f9f9' : 'white',
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...(!atleta.isPlaceholder ? { ...attributes, ...listeners } : {})}
      sx={{
        p: 2,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {atleta.isPlaceholder ? (
        <Typography sx={{ color: '#ccc', width: 250, textAlign: 'center' }}>
          Vacío
        </Typography>
      ) : (
        <>
          <Typography sx={{ width: 120 }}>{atleta.position}</Typography>
          <Typography sx={{ width: 200 }}>
            {`${toText(atleta.nombre, '')} ${toText(atleta.apellido, '')}`.trim()}
          </Typography>
          <Typography sx={{ width: 100 }}>{toText(atleta.categoria)}</Typography>
          {(() => {
            const ramaTxt = toText(atleta.rama, '');
            const rama = ramaTxt.toLowerCase();
            const color =
              rama === 'varonil' ? 'primary' : rama === 'femenil' ? 'secondary' : 'default';
            return <Chip label={ramaTxt || '—'} color={color} size="small" />;
          })()}
        </>
      )}
    </Paper>
  );
};

/* ----------------------------------------------------------------------
   Componente RelayRow
---------------------------------------------------------------------- */
const RelayRow = ({ relay, onUpdatePositions }) => {
  const [open, setOpen] = useState(false);
  const defaultSlots = 4;

  // Función para rellenar con placeholders hasta tener defaultSlots elementos.
  const fillSlots = (items, group) => {
    const filled = [...items];
    while (filled.length < defaultSlots) {
      filled.push({
        id: `placeholder-${group}-${filled.length}`,
        isPlaceholder: true,
      });
    }
    return filled;
  };

  // Separa los inscritos en "Integrantes" y "Suplentes" según la propiedad "posicion"
  const segregateAtletas = useCallback(() => {
    const all = Array.isArray(relay.atletasData) ? relay.atletasData : [];
    const integrantes = all.filter(
      (item) => !item.posicion || item.posicion.startsWith('Integrante')
    );
    const suplentes = all.filter(
      (item) => item.posicion && item.posicion.startsWith('Suplente')
    );
    return { integrantes, suplentes };
  }, [relay.atletasData]);

  // Estado local para integrantes y suplentes
  const [{ integrantes, suplentes }, setLocalAtletas] = useState(() => {
    const { integrantes, suplentes } = segregateAtletas();
    return {
      integrantes: fillSlots(integrantes, 'integrantes'),
      suplentes: fillSlots(suplentes, 'suplentes'),
    };
  });

  const [changesMade, setChangesMade] = useState(false);

  // Si relay.atletasData cambia (p.ej. se agregan nuevos inscritos), recargamos el estado local
  useEffect(() => {
    const { integrantes, suplentes } = segregateAtletas();
    setLocalAtletas({
      integrantes: fillSlots(integrantes, 'integrantes'),
      suplentes: fillSlots(suplentes, 'suplentes'),
    });
  }, [segregateAtletas]);

  // Función que actualiza la numeración ("Integrante 1", "Suplente 2", etc.)
  const updatePositions = (list, group) =>
    list.map((item, index) =>
      item.isPlaceholder
        ? item
        : {
            ...item,
            position: group === 'integrantes'
              ? `Integrante ${index + 1}`
              : `Suplente ${index + 1}`,
          }
    );

  // Handler de drag & drop
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeContainer = active.data.current.sortable.containerId;
    const overContainer = over.data.current.sortable.containerId;

    if (activeContainer === overContainer) {
      if (activeContainer === 'integrantes') {
        const oldIndex = integrantes.findIndex((item) => item.id === active.id);
        const newIndex = integrantes.findIndex((item) => item.id === over.id);
        if (newIndex < 0) return;
        const newList = arrayMove(integrantes, oldIndex, newIndex);
        const updated = updatePositions(newList.filter((item) => !item.isPlaceholder), 'integrantes');
        setLocalAtletas((prev) => ({
          ...prev,
          integrantes: fillSlots(updated, 'integrantes'),
        }));
        setChangesMade(true);
      } else if (activeContainer === 'suplentes') {
        const oldIndex = suplentes.findIndex((item) => item.id === active.id);
        const newIndex = suplentes.findIndex((item) => item.id === over.id);
        if (newIndex < 0) return;
        const newList = arrayMove(suplentes, oldIndex, newIndex);
        const updated = updatePositions(newList.filter((item) => !item.isPlaceholder), 'suplentes');
        setLocalAtletas((prev) => ({
          ...prev,
          suplentes: fillSlots(updated, 'suplentes'),
        }));
        setChangesMade(true);
      }
    } else {
      // Integrantes -> Suplentes
      if (activeContainer === 'integrantes' && overContainer === 'suplentes') {
        const activeIndex = integrantes.findIndex((item) => item.id === active.id);
        if (activeIndex < 0) return;
        const movedItem = integrantes[activeIndex];
        const newIntegrantes = [...integrantes];
        newIntegrantes.splice(activeIndex, 1);
        const overIndex = suplentes.findIndex((item) => item.id === over.id);
        const newSuplentes = [...suplentes];
        newSuplentes.splice(overIndex, 0, movedItem);

        const updatedIntegrantes = updatePositions(newIntegrantes.filter((item) => !item.isPlaceholder), 'integrantes');
        const updatedSuplentes = updatePositions(newSuplentes.filter((item) => !item.isPlaceholder), 'suplentes');
        setLocalAtletas({
          integrantes: fillSlots(updatedIntegrantes, 'integrantes'),
          suplentes: fillSlots(updatedSuplentes, 'suplentes'),
        });
        setChangesMade(true);
      }
      // Suplentes -> Integrantes
      else if (activeContainer === 'suplentes' && overContainer === 'integrantes') {
        const activeIndex = suplentes.findIndex((item) => item.id === active.id);
        if (activeIndex < 0) return;
        const movedItem = suplentes[activeIndex];
        const newSuplentes = [...suplentes];
        newSuplentes.splice(activeIndex, 1);
        const overIndex = integrantes.findIndex((item) => item.id === over.id);
        const newIntegrantes = [...integrantes];
        newIntegrantes.splice(overIndex, 0, movedItem);

        const updatedIntegrantes = updatePositions(newIntegrantes.filter((item) => !item.isPlaceholder), 'integrantes');
        const updatedSuplentes = updatePositions(newSuplentes.filter((item) => !item.isPlaceholder), 'suplentes');
        setLocalAtletas({
          integrantes: fillSlots(updatedIntegrantes, 'integrantes'),
          suplentes: fillSlots(updatedSuplentes, 'suplentes'),
        });
        setChangesMade(true);
      }
    }
  };

  // Guarda los cambios (onUpdatePositions) en la fuente de datos real (padre o contexto)
  const handleSaveChanges = () => {
    const updatedAtletasData = [
      ...updatePositions(integrantes.filter((item) => !item.isPlaceholder), 'integrantes'),
      ...updatePositions(suplentes.filter((item) => !item.isPlaceholder), 'suplentes'),
    ];
    onUpdatePositions(relay, updatedAtletasData);
    setChangesMade(false);
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{relay.prueba}</TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              src={
                relay?.equipo && typeof relay.equipo === 'object' ? relay.equipo.logoEquipo || '' : ''
              }
              sx={{ width: 30, height: 30 }}
            >
              {toText(relay?.equipo, '').charAt(0)}
            </Avatar>
            <Typography>{toText(relay?.equipo)}</Typography>
          </Box>
        </TableCell>
        <TableCell>{toText(relay.categoria)}</TableCell>
        <TableCell>
          {(() => {
            const ramaTxt = toText(relay.rama, '');
            const rama = ramaTxt.toLowerCase();
            const color =
              rama === 'varonil' ? 'primary' : rama === 'femenil' ? 'secondary' : 'default';
            return <Chip label={ramaTxt || '—'} color={color} size="small" />;
          })()}
        </TableCell>
        <TableCell>{relay.asignacion || '-'}</TableCell>
        <TableCell>{formatMeetEntryMark(relay)}</TableCell>
        <TableCell align="center">
          {changesMade && (
            <IconButton onClick={handleSaveChanges}>
              <Save color="primary" />
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      {/* Fila expandible con listas de Integrantes y Suplentes */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom>
                Inscritos del Relevo
              </Typography>
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1">Integrantes</Typography>
                  <SortableContext items={integrantes.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                    {integrantes.map((item) => (
                      <SortableAtleta key={item.id} atleta={item} containerId="integrantes" />
                    ))}
                  </SortableContext>
                </Box>
                <Box>
                  <Typography variant="subtitle1">Suplentes</Typography>
                  <SortableContext items={suplentes.map((item) => item.id)} strategy={verticalListSortingStrategy}>
                    {suplentes.map((item) => (
                      <SortableAtleta key={item.id} atleta={item} containerId="suplentes" />
                    ))}
                  </SortableContext>
                </Box>
              </DndContext>
              {changesMade && (
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Button variant="contained" color="primary" onClick={handleSaveChanges}>
                    Guardar cambios
                  </Button>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/* ----------------------------------------------------------------------
   Componente principal: RelaysTable
   Ahora incluye barra de búsqueda con un Select y un TextField
---------------------------------------------------------------------- */
const RelaysTable = () => {
  const { slug, competitionId } = useParams();
  const { competencias } = useCompetencia();
  const competition = useMemo(() => (
    competencias.find((comp) => comp.id === competitionId) ||
    competencias.find((comp) => comp.slug === slug)
  ), [competencias, competitionId, slug]);

  // Función para agrupar relevos a partir de competition.inscripciones
  const getRelayEntries = useCallback(() => {
    if (!competition?.inscripciones) return [];
    const relayGroups = {};
    competition.inscripciones.forEach((inscription) => {
      if (Array.isArray(inscription.relevosData)) {
        inscription.relevosData.forEach((relay) => {
          if (!relay.prueba) return;
          let teamCode = 'UNA';
          let teamName = 'Unattached';
          let teamLogo = '';

          if (inscription.equipo && typeof inscription.equipo === 'object') {
            teamCode = inscription.equipo.teamCode || 'UNA';
            teamName = inscription.equipo.name || 'Unattached';
            teamLogo = inscription.equipo.logoEquipo || '';
          } else if (typeof inscription.equipo === 'string') {
            teamName = inscription.equipo;
          }

          const relayGender = relay.equipoTipo || inscription.rama;
          const relayKey = `${teamCode}|${relay.asignacion || 'A'}|${relayGender}|${
            relay.eventCode || ''
          }|${inscription.catNumber || ''}`;

          if (!relayGroups[relayKey]) {
            relayGroups[relayKey] = {
              id: relayKey,
              prueba: relay.prueba,
              equipoLogo: teamLogo,
              equipo: teamName,
              categoria: inscription.categoria,
              rama: relay.equipoTipo || inscription.rama,
              asignacion: relay.asignacion || 'A',
              eventCode: relay.eventCode,
              marcaMinutos: relay.marcaMinutos,
              marcaSegundos: relay.marcaSegundos,
              marcaCentesimas: relay.marcaCentesimas,
              tipoMarca: relay.tipoMarca,
              atletasData: [],
            };
          }

          relayGroups[relayKey].atletasData.push({
            id: String(inscription.id),
            nombre: inscription.nombre,
            apellido: inscription.apellido,
            categoria: inscription.categoria,
            rama: inscription.rama,
            posicion: relay.posicion || '',
          });
        });
      }
    });
    return Object.values(relayGroups);
  }, [competition]);

  // Estado local con los relevos ya agrupados
  const [relayEntries, setRelayEntries] = useState([]);

  // Cargamos o recalculamos los relevos cuando cambia la competencia
  useEffect(() => {
    setRelayEntries(getRelayEntries());
  }, [competition, getRelayEntries]);

  // Actualiza la posición de los atletas (drag & drop) en el estado local
  const handleUpdatePositions = (relay, updatedAtletas) => {
    setRelayEntries((prev) =>
      prev.map((r) => (r.id === relay.id ? { ...r, atletasData: updatedAtletas } : r))
    );
  };

  // Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // ----- FILTRO -----
  const [filterField, setFilterField] = useState('all');
  const [filterText, setFilterText] = useState('');

  // Lógica para filtrar los relevos
  const filteredRelayEntries = useMemo(() => {
    if (!filterText) return relayEntries;
    const lowerText = filterText.toLowerCase();

    return relayEntries.filter((relay) => {
      // Obtenemos el nombre del equipo (si es objeto, usamos .name)
      const equipoName = typeof relay.equipo === 'object' ? relay.equipo.name : relay.equipo || '';
      const ramaLower = relay.rama?.toLowerCase() || '';
      const categoriaLower = relay.categoria?.toLowerCase() || '';
      const pruebaLower = relay.prueba?.toLowerCase() || '';

      // "nombre" => verificar si alguno de los atletasData coincide
      const atletasMatch = relay.atletasData?.some((a) =>
        `${a.nombre} ${a.apellido}`.toLowerCase().includes(lowerText)
      );

      switch (filterField) {
        case 'relevo':
          return pruebaLower.includes(lowerText);
        case 'equipo':
          return equipoName.toLowerCase().includes(lowerText);
        case 'rama':
          return ramaLower.includes(lowerText);
        case 'categoria':
          return categoriaLower.includes(lowerText);
        case 'nombre':
          return atletasMatch;
        default: // 'all'
          // Comprobamos si coincide en cualquiera de los campos
          return (
            pruebaLower.includes(lowerText) ||
            equipoName.toLowerCase().includes(lowerText) ||
            ramaLower.includes(lowerText) ||
            categoriaLower.includes(lowerText) ||
            atletasMatch
          );
      }
    });
  }, [relayEntries, filterField, filterText]);

  // Relevos paginados
  const paginatedRelayEntries = useMemo(() => {
    return filteredRelayEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredRelayEntries, page, rowsPerPage]);

  return (
    <>
      {/* Barra de búsqueda */}
      <Box
        sx={{
          maxWidth: 1200,
          margin: 'auto',
          mt: 2,
          mb: 2,
          px: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 150 } }}>
          <InputLabel id="filter-field-label">Filtrar por</InputLabel>
          <Select
            labelId="filter-field-label"
            label="Filtrar por"
            value={filterField}
            onChange={(e) => setFilterField(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="relevo">Relevo</MenuItem>
            <MenuItem value="equipo">Equipo</MenuItem>
            <MenuItem value="rama">Rama</MenuItem>
            <MenuItem value="categoria">Categoría</MenuItem>
            <MenuItem value="nombre">Nombre</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Buscar"
          placeholder="Ingrese texto para filtrar"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          size="small"
        />
      </Box>

      <TableContainer component={Paper} sx={{ maxWidth: 1200, margin: 'auto', mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="50px" />
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Prueba
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Equipo
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Categoría
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Rama
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Asignación
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Marca
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="subtitle1" fontWeight="bold">
                  Acciones
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRelayEntries.length > 0 ? (
              paginatedRelayEntries.map((relay) => (
                <RelayRow
                  key={relay.id}
                  relay={relay}
                  onUpdatePositions={handleUpdatePositions}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2">
                    No hay relevos que coincidan con la búsqueda.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={filteredRelayEntries.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </>
  );
};

export default RelaysTable;
